import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Vibrate, VibrateOff } from 'lucide-react';
import { shared } from '../three/shared';
import { useStore } from '../store';

// Minimalist gold D-Pad (bottom-left). Up/Down = drive, Left/Right = steer.
export default function TouchControls() {
  const [isTouch, setIsTouch] = useState(false);
  const joysticks = useStore((s) => s.joysticks);
  const haptics = useStore((s) => s.haptics);
  const toggleHaptics = useStore((s) => s.toggleHaptics);
  const mode = useStore((s) => s.mode);
  const activeSideCard = useStore((s) => s.activeSideCard);
  const tourActive = useStore((s) => s.tourActive);
  const sideCardOpen = activeSideCard !== null && activeSideCard !== undefined;
  // Hard rule: hide the D-Pad + HAPTICS entirely whenever an info card is open
  // or the guided tour is auto-flying — so they never sit over the zone content.
  const hideControls = sideCardOpen || tourActive;

  useEffect(() => {
    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0 ||
      new URLSearchParams(window.location.search).get('touch') === '1';
    setIsTouch(touch);
  }, []);

  // Release any held input the moment the D-Pad is hidden so the vehicle
  // never keeps driving behind the panel.
  useEffect(() => {
    if (hideControls) { shared.input.forward = 0; shared.input.turn = 0; shared.input.up = 0; }
  }, [hideControls]);

  if (!isTouch && !joysticks) return null;
  if (hideControls) return null;

  const buzz = () => { if (haptics && typeof navigator !== 'undefined' && navigator.vibrate) { try { navigator.vibrate(15); } catch (e) {} } };

  const hold = (fn) => ({
    onPointerDown: (e) => { e.preventDefault(); buzz(); fn(); },
    onPointerUp: () => { shared.input.forward = 0; shared.input.turn = 0; },
    onPointerLeave: () => { shared.input.forward = 0; shared.input.turn = 0; },
    onPointerCancel: () => { shared.input.forward = 0; shared.input.turn = 0; },
  });

  // 20% larger tap targets for comfortable finger use on iPhone / iPad.
  const Btn = ({ testid, onHold, children, cls }) => (
    <button
      data-testid={testid}
      {...hold(onHold)}
      className={`w-14 h-14 flex items-center justify-center rounded-lg glass border border-gold/40 text-gold active:bg-gold active:text-black transition-colors touch-none ${cls || ''}`}
    >
      {children}
    </button>
  );

  const alt = (v) => ({
    onPointerDown: () => { buzz(); shared.input.up = v; },
    onPointerUp: () => (shared.input.up = 0),
    onPointerLeave: () => (shared.input.up = 0),
  });

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] pointer-events-none select-none">
      <div className="relative flex justify-center items-end px-5 pb-2">
        {/* D-Pad — bottom-CENTER (beneath the river), clear of left-side zone info */}
        <div className="flex flex-col items-center gap-2">
          <button
            data-testid="haptics-toggle"
            onClick={toggleHaptics}
            title={haptics ? 'Vibration on — tap to turn off' : 'Vibration off — tap to turn on'}
            className={`pointer-events-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-full glass text-[9px] font-bold uppercase tracking-widest transition-colors ${haptics ? 'text-gold border-gold/50' : 'text-white/50'}`}
          >
            {haptics ? <Vibrate className="w-3.5 h-3.5" /> : <VibrateOff className="w-3.5 h-3.5" />}
            Haptics
          </button>
          <div data-testid="dpad" className="pointer-events-auto grid grid-cols-3 grid-rows-3 gap-1.5" style={{ width: 186 }}>
          <div />
          <Btn testid="dpad-up" onHold={() => (shared.input.forward = 1)}><ChevronUp className="w-7 h-7" strokeWidth={2.4} /></Btn>
          <div />
          <Btn testid="dpad-left" onHold={() => (shared.input.turn = 1)}><ChevronLeft className="w-7 h-7" strokeWidth={2.4} /></Btn>
          <div className="w-14 h-14 flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-gold/50" /></div>
          <Btn testid="dpad-right" onHold={() => (shared.input.turn = -1)}><ChevronRight className="w-7 h-7" strokeWidth={2.4} /></Btn>
          <div />
          <Btn testid="dpad-down" onHold={() => (shared.input.forward = -1)}><ChevronDown className="w-7 h-7" strokeWidth={2.4} /></Btn>
          <div />
          </div>
        </div>

        {/* Altitude (flight only) — pinned bottom-right, out of the D-Pad's way */}
        {mode === 'helicopter' && (
          <div className="absolute right-5 bottom-2 flex flex-col gap-2.5 pointer-events-auto">
            <button {...alt(1)} data-testid="altitude-up-btn" className="w-14 h-14 flex items-center justify-center rounded-lg glass-gold text-gold active:bg-gold active:text-black touch-none">
              <ChevronUp className="w-7 h-7" strokeWidth={2.4} />
            </button>
            <button {...alt(-1)} data-testid="altitude-down-btn" className="w-14 h-14 flex items-center justify-center rounded-lg glass-gold text-gold active:bg-gold active:text-black touch-none">
              <ChevronDown className="w-7 h-7" strokeWidth={2.4} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
